import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, type OnChanges, type OnInit, type SimpleChanges } from '@angular/core';
import { UnitService } from '@zaoblako/web/core/services/finance/unit.service';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { StandardMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/standard-dropdown/standard-multi-select/standard-multi-select.component';
import { reconcileSelectionByNumericIds } from '@zaoblako/web/shared/helpers/dropdown-catalog-selection-reconcile.helper';
import type { UnitFindInterface } from '@zaoblako/shared/modules/catalog/dtos/unit/unit-find.dto';
import type { FiltersUiVersion } from '@zaoblako/web/core/types/filters-ui-version.type';

/**
 * @deprecated Use `crm-standard-multi-select` and `UnitService.dropdownDescriptorUrl`.
 */
@Component({
  selector: 'crm-unit-multi-select',
  standalone: true,
  imports: [MultiSelectComponent, StandardMultiSelectComponent],
  templateUrl: './unit-multi-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UnitMultiSelectComponent implements OnInit, OnChanges {
  @Input() public filtersUiVersion: FiltersUiVersion = 'v2';
  @Input() public units: UnitFindInterface[] = [];
  @Input() public selectedUnits: UnitFindInterface[] = [];
  @Output() public selectedUnitsChange = new EventEmitter<UnitFindInterface[]>();
  @Input() public customRequest = false;
  @Input() public sort: string;
  @Input() public class: string;
  @Input() public styleClass: string;
  @Input() public showClear = true;
  @Input() public disabled = false;
  @Input() public isError = false;
  @Input() public isFloatLabel = true;
  @Input() public placeholder = '\u00A0';
  @Input() public display = 'chip';
  @Input() public appendTo: string;
  @Input() public itemStyleClass: string;

  /** Set only after `setSelectedUnitIds`. `null` means the filter input is not used. */
  public unitIds: number[] | null = null;
  public loading = false;
  public selectionLoading = false;
  public useStandard = false;
  public readonly descriptorUrl: string;
  private inputsReady = false;

  public constructor(
    unitService: UnitService,
    private readonly globalService: GlobalService
  ) {
    this.descriptorUrl = unitService.dropdownDescriptorUrl;
  }

  @Input() public set setSelectedUnitIds(ids: number[] | null | undefined) {
    this.unitIds = (ids ?? []).map((id) => (typeof id === 'string' ? Number(id) : id));
    if (this.inputsReady && !this.useStandard) {
      this.applyCustomSelection();
    }
  }

  public ngOnInit(): void {
    this.useStandard = !this.customRequest && !!this.globalService.globalInfo?.tenantInfo?.ratePlan?.modulesAccessControl?.BASIS?.enabled;
    this.inputsReady = true;
    if (!this.useStandard) {
      this.applyCustomSelection();
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!this.useStandard && changes.units && !changes.units.isFirstChange()) {
      this.applyCustomSelection();
    }
  }

  public onSelected = (units: UnitFindInterface[]): void => {
    this.selectedUnits = units ?? [];
    this.selectedUnitsChange.emit(this.selectedUnits);
  };

  private applyCustomSelection = (): void => {
    const ids = this.unitIds;
    if (!ids?.length) {
      this.selectionLoading = false;
      return;
    }

    const { selected, hasStaleKeys } = reconcileSelectionByNumericIds(this.units, ids);
    const selectionDiffers =
      !this.selectedUnits?.length ||
      this.selectedUnits.length !== selected.length ||
      !this.selectedUnits.every((unit) => selected.some((item) => item.id === unit.id));

    if (!selectionDiffers && !hasStaleKeys) {
      this.selectionLoading = false;
      return;
    }

    this.selectedUnits = selected;
    this.selectionLoading = false;
    if (hasStaleKeys) {
      this.onSelected(selected);
    }
  };
}
